import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api/axios";
import { getDepartments, getRoles } from "../../api/organizationApi";
import { getBloodGroups } from "../../api/employeeApi";

import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

const COMPANY_NAME = "Quandatum Analytics";
const GRADES = ["Senior", "Junior", "Intern"];
const GENDERS = ["MALE", "FEMALE", "OTHER"];

const inputClass =
  "border rounded px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500";

const Label = ({ text, required }) => (
  <label className="text-sm font-medium text-gray-700 mb-1 block">
    {text} {required && <span className="text-red-600">*</span>}
  </label>
);

/* ─────────────── Success Modal ─────────────── */
const SuccessModal = ({ data, onClose }) => {
  const isAuto = data.password_type === "auto";

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert("Password copied to clipboard!");
    });
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: "12px",
          padding: "36px",
          maxWidth: "480px",
          width: "90%",
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
          position: "relative",
        }}
      >
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: "20px" }}>
          <div
            style={{
              width: "60px",
              height: "60px",
              background: "#d1fae5",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 12px",
              fontSize: "28px",
            }}
          >
            ✅
          </div>
          <h2 style={{ fontSize: "20px", fontWeight: "700", color: "#1a202c", margin: 0 }}>
            Employee Created Successfully
          </h2>
        </div>

        {/* Details */}
        <div
          style={{
            background: "#f8fafc",
            borderRadius: "8px",
            padding: "16px",
            marginBottom: "16px",
            fontSize: "14px",
            color: "#374151",
          }}
        >
          <div style={{ marginBottom: "8px" }}>
            <span style={{ fontWeight: "600" }}>Employee ID: </span>
            <span style={{ color: "#2563eb", fontWeight: "700" }}>{data.employee_id}</span>
          </div>
          <div style={{ marginBottom: isAuto ? "12px" : "0" }}>
            <span style={{ fontWeight: "600" }}>Email: </span>
            <span>{data.email}</span>
          </div>

          {isAuto && data.generated_password && (
            <>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  background: "#fffbeb",
                  border: "1px solid #fcd34d",
                  borderRadius: "6px",
                  padding: "10px 14px",
                  marginTop: "8px",
                }}
              >
                <div style={{ flex: 1 }}>
                  <span style={{ fontWeight: "600" }}>Generated Password: </span>
                  <span
                    style={{
                      fontFamily: "monospace",
                      fontSize: "15px",
                      color: "#b45309",
                      fontWeight: "700",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {data.generated_password}
                  </span>
                </div>
                <button
                  onClick={() => copyToClipboard(data.generated_password)}
                  style={{
                    background: "#f59e0b",
                    color: "#fff",
                    border: "none",
                    borderRadius: "4px",
                    padding: "5px 10px",
                    fontSize: "12px",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    fontWeight: "600",
                  }}
                >
                  Copy
                </button>
              </div>
              <p
                style={{
                  fontSize: "12px",
                  color: "#6b7280",
                  marginTop: "8px",
                  marginBottom: 0,
                  fontStyle: "italic",
                }}
              >
                ⚠️ Please copy and share this password with the employee if
                email delivery fails.
              </p>
            </>
          )}

          {!isAuto && (
            <div style={{ marginTop: "8px", color: "#059669", fontWeight: "600" }}>
              ✔ Login credentials have been created successfully.
            </div>
          )}
        </div>

        <button
          onClick={onClose}
          style={{
            background: "#2563eb",
            color: "#fff",
            border: "none",
            borderRadius: "8px",
            padding: "12px 0",
            width: "100%",
            fontSize: "15px",
            fontWeight: "600",
            cursor: "pointer",
          }}
        >
          Go to Employee List
        </button>
      </div>
    </div>
  );
};

/* ─────────────── Main Component ─────────────── */
const AddEmployee = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [successData, setSuccessData] = useState(null); // drives modal

  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [bloodGroups, setBloodGroups] = useState([]);

  // Password Setup state
  const [passwordType, setPasswordType] = useState("auto"); // "auto" | "manual"
  const [manualPassword, setManualPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordErrors, setPasswordErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [formData, setFormData] = useState({
    email: "",
    full_name: "",
    department: "",
    role: "",
    grade: "",
    gender: "",
    blood_group: "",
    date_of_birth: "",
    date_of_joining: "",
    phone_number: "",
    pancard_number: "",
    aadhaar_number: "",
    current_address: "",
    permanent_address: "",
    photo: null,

    bank_detail: {
      bank_name: "",
      account_number: "",
      ifsc_code: "",
    },

    family_members: [
      { name: "", relationship: "", phone_number: "" },
    ],
  });

  /* ================= LOAD DROPDOWNS ================= */
  useEffect(() => {
    getDepartments().then((res) => setDepartments(res.data || []));
    getRoles().then((res) => setRoles(res.data || []));
    getBloodGroups().then((res) => setBloodGroups(res.data || []));
  }, []);

  /* ================= HANDLERS ================= */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFormData((p) => ({ ...p, photo: file }));
    setPhotoPreview(URL.createObjectURL(file));
  };

  const addFamilyMember = () => {
    setFormData((p) => ({
      ...p,
      family_members: [
        ...p.family_members,
        { name: "", relationship: "", phone_number: "" },
      ],
    }));
  };

  const updateFamilyMember = (i, field, value) => {
    const updated = [...formData.family_members];
    updated[i][field] = value;
    setFormData((p) => ({ ...p, family_members: updated }));
  };

  /* ================= PASSWORD VALIDATION ================= */
  const validatePasswords = () => {
    if (passwordType === "auto") return true;

    const errors = {};
    if (!manualPassword) {
      errors.manualPassword = "Password is required.";
    } else if (manualPassword.length < 8) {
      errors.manualPassword = "Password must be at least 8 characters.";
    }
    if (!confirmPassword) {
      errors.confirmPassword = "Please confirm the password.";
    } else if (manualPassword !== confirmPassword) {
      errors.confirmPassword = "Passwords do not match.";
    }
    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!validatePasswords()) return;

    try {
      setLoading(true);

      const payload = new FormData();

      payload.append("email", formData.email);
      payload.append("full_name", formData.full_name);
      payload.append("department", formData.department);
      payload.append("role", formData.role);
      payload.append("grade", formData.grade);
      payload.append("gender", formData.gender);
      payload.append("date_of_joining", formData.date_of_joining);

      if (formData.date_of_birth)
        payload.append("date_of_birth", formData.date_of_birth);

      payload.append("phone_number", formData.phone_number);
      payload.append("blood_group", formData.blood_group);
      payload.append("pancard_number", formData.pancard_number);
      payload.append("aadhaar_number", formData.aadhaar_number);
      payload.append("current_address", formData.current_address);
      payload.append("permanent_address", formData.permanent_address);

      if (formData.photo)
        payload.append("photo", formData.photo);

      payload.append(
        "bank_detail_input",
        JSON.stringify(formData.bank_detail)
      );

      const validFamily = formData.family_members.filter(
        (m) => m.name || m.relationship || m.phone_number
      );

      payload.append(
        "family_members_input",
        JSON.stringify(validFamily)
      );

      // Password setup fields
      payload.append("password_type", passwordType);
      if (passwordType === "manual") {
        payload.append("manual_password", manualPassword);
      }

      const res = await api.post("/employees/create/", payload);
      setSuccessData(res.data); // open success modal
    } catch (error) {
      console.error("CREATE EMPLOYEE ERROR:", error.response?.data);
      alert(JSON.stringify(error.response?.data, null, 2));
    } finally {
      setLoading(false);
    }
  };

  /* ================= UI ================= */
  return (
    <>
      {/* Success Modal */}
      {successData && (
        <SuccessModal
          data={successData}
          onClose={() => {
            setSuccessData(null);
            navigate("/admin/employees");
          }}
        />
      )}

      <div className="max-w-5xl mx-auto bg-white p-8 shadow rounded">
        <h1 className="text-2xl font-bold mb-6">Add Employee</h1>

        <form onSubmit={handleSubmit} className="space-y-8">

          {/* BASIC DETAILS */}
          <section>
            <h2 className="text-lg font-semibold mb-4 text-blue-600">
              Basic Details
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label text="Email" required />
                <input name="email" type="email" required onChange={handleChange} className={inputClass} />
              </div>

              <div>
                <Label text="Full Name" required />
                <input name="full_name" required onChange={handleChange} className={inputClass} />
              </div>
              <div>
                <Label text="Company Name" />
                <input
                  value={COMPANY_NAME}
                  disabled
                  className={inputClass + " bg-gray-100 cursor-not-allowed"}
                />
              </div>

              <div>
                <Label text="Gender" />
                <select name="gender" onChange={handleChange} className={inputClass}>
                  <option value="">Select Gender</option>
                  {GENDERS.map((g) => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label text="Date of Birth" />
                <input type="date" name="date_of_birth" onChange={handleChange} className={inputClass} />
              </div>

              <div>
                <Label text="Department" required />
                <select
                  name="department"
                  required
                  onChange={handleChange}
                  className={inputClass}
                >
                  <option value="">Select Department</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.name}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label text="Role" required />
                <select
                  name="role"
                  required
                  onChange={handleChange}
                  className={inputClass}
                >
                  <option value="">Select Role</option>
                  {roles.map((r) => (
                    <option key={r.id} value={r.name}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label text="Grade" required />
                <select name="grade" required onChange={handleChange} className={inputClass}>
                  <option value="">Select Grade</option>
                  {GRADES.map((g) => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label text="Phone Number" />
                <PhoneInput
                  country="in"
                  value={formData.phone_number.replace("+", "")}
                  onChange={(v) =>
                    setFormData((p) => ({ ...p, phone_number: `+${v}` }))
                  }
                  inputStyle={{ width: "100%" }}
                />
              </div>

              <div>
                <Label text="Blood Group" />
                <select name="blood_group" onChange={handleChange} className={inputClass}>
                  <option value="">Select Blood Group</option>
                  {bloodGroups.map((bg) => (
                    <option key={bg.value} value={bg.value}>{bg.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label text="Date of Joining" required />
                <input type="date" name="date_of_joining" required onChange={handleChange} className={inputClass} />
              </div>
            </div>
          </section>

          {/* ADDRESS */}
          <section>
            <h2 className="text-lg font-semibold mb-4">Address</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <textarea name="current_address" placeholder="Current Address" onChange={handleChange} className={inputClass} />
              <textarea name="permanent_address" placeholder="Permanent Address" onChange={handleChange} className={inputClass} />
            </div>
          </section>

          {/* PHOTO */}
          <section>
            <h2 className="text-lg font-semibold mb-4">Profile Photo</h2>
            <div className="flex gap-6 items-center">
              <img
                src={photoPreview || "/default-avatar.png"}
                alt="Preview"
                className="w-24 h-24 rounded-full border object-cover"
              />
              <input type="file" accept="image/*" onChange={handlePhotoChange} />
            </div>
          </section>

          {/* BANK */}
          <section>
            <h2 className="text-lg font-semibold mb-4">Bank Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <input placeholder="Bank Name" className={inputClass}
                onChange={(e) =>
                  setFormData((p) => ({
                    ...p,
                    bank_detail: { ...p.bank_detail, bank_name: e.target.value },
                  }))
                }
              />
              <input placeholder="Account Number" className={inputClass}
                onChange={(e) =>
                  setFormData((p) => ({
                    ...p,
                    bank_detail: { ...p.bank_detail, account_number: e.target.value },
                  }))
                }
              />
              <input placeholder="IFSC Code" className={inputClass}
                onChange={(e) =>
                  setFormData((p) => ({
                    ...p,
                    bank_detail: { ...p.bank_detail, ifsc_code: e.target.value },
                  }))
                }
              />
            </div>
          </section>

          {/* ID PROOF */}
          <section>
            <h2 className="text-lg font-semibold mb-4">
              ID Proof <span className="text-sm text-gray-500">(Optional)</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input name="pancard_number" value={formData.pancard_number} onChange={handleChange} className={inputClass} placeholder="PAN Number" />
              <input name="aadhaar_number" value={formData.aadhaar_number} onChange={handleChange} className={inputClass} placeholder="Aadhaar Number" />
            </div>
          </section>

          {/* FAMILY */}
          <section>
            <h2 className="text-lg font-semibold mb-4">Family Members</h2>

            {formData.family_members.map((m, i) => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                <input className={inputClass} placeholder="Name"
                  value={m.name}
                  onChange={(e) => updateFamilyMember(i, "name", e.target.value)}
                />
                <input className={inputClass} placeholder="Relationship"
                  value={m.relationship}
                  onChange={(e) => updateFamilyMember(i, "relationship", e.target.value)}
                />
                <PhoneInput
                  country="in"
                  value={m.phone_number.replace("+", "")}
                  onChange={(v) =>
                    updateFamilyMember(i, "phone_number", `+${v}`)
                  }
                  inputStyle={{ width: "100%" }}
                />
              </div>
            ))}

            <button type="button" onClick={addFamilyMember} className="text-blue-600 font-medium">
              + Add Family Member
            </button>
          </section>

          {/* ──────────── PASSWORD SETUP ──────────── */}
          <section
            style={{
              border: "1.5px solid #dbeafe",
              borderRadius: "10px",
              padding: "24px",
              background: "#f0f7ff",
            }}
          >
            <h2
              style={{
                fontSize: "16px",
                fontWeight: "700",
                color: "#1d4ed8",
                marginBottom: "16px",
              }}
            >
              Password Setup <span style={{ color: "#dc2626" }}>*</span>
            </h2>

            {/* Radio options */}
            <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginBottom: "20px" }}>

              {/* Auto */}
              <label
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "12px",
                  cursor: "pointer",
                  padding: "14px 16px",
                  borderRadius: "8px",
                  border: passwordType === "auto" ? "2px solid #2563eb" : "1.5px solid #d1d5db",
                  background: passwordType === "auto" ? "#eff6ff" : "#fff",
                  transition: "all 0.2s",
                }}
              >
                <input
                  type="radio"
                  name="passwordType"
                  value="auto"
                  checked={passwordType === "auto"}
                  onChange={() => {
                    setPasswordType("auto");
                    setPasswordErrors({});
                  }}
                  style={{ marginTop: "2px", accentColor: "#2563eb" }}
                />
                <div>
                  <div style={{ fontWeight: "600", color: "#1e293b", fontSize: "14px" }}>
                    Auto Generate Password
                  </div>
                  <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "2px" }}>
                    System generates a secure random password and emails it to the employee.
                    The generated password will also be shown to you here.
                  </div>
                </div>
              </label>

              {/* Manual */}
              <label
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "12px",
                  cursor: "pointer",
                  padding: "14px 16px",
                  borderRadius: "8px",
                  border: passwordType === "manual" ? "2px solid #2563eb" : "1.5px solid #d1d5db",
                  background: passwordType === "manual" ? "#eff6ff" : "#fff",
                  transition: "all 0.2s",
                }}
              >
                <input
                  type="radio"
                  name="passwordType"
                  value="manual"
                  checked={passwordType === "manual"}
                  onChange={() => {
                    setPasswordType("manual");
                    setPasswordErrors({});
                  }}
                  style={{ marginTop: "2px", accentColor: "#2563eb" }}
                />
                <div>
                  <div style={{ fontWeight: "600", color: "#1e293b", fontSize: "14px" }}>
                    Manual Create Password
                  </div>
                  <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "2px" }}>
                    Set a specific password for the employee. They can log in using this password.
                  </div>
                </div>
              </label>
            </div>

            {/* Manual password fields */}
            {passwordType === "manual" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                {/* Password */}
                <div>
                  <Label text="Password" required />
                  <div style={{ position: "relative" }}>
                    <input
                      type={showPassword ? "text" : "password"}
                      value={manualPassword}
                      onChange={(e) => {
                        setManualPassword(e.target.value);
                        setPasswordErrors((p) => ({ ...p, manualPassword: "" }));
                      }}
                      placeholder="Minimum 8 characters"
                      className={inputClass}
                      style={{
                        paddingRight: "40px",
                        ...(passwordErrors.manualPassword ? { borderColor: "#dc2626" } : {}),
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      style={{
                        position: "absolute",
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: "0",
                        color: "#6b7280",
                        display: "flex",
                        alignItems: "center",
                      }}
                      tabIndex={-1}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? (
                        /* Eye-off icon */
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-5 0-9-4-9-7a9.77 9.77 0 012.168-3.582M6.343 6.343A9.956 9.956 0 0112 5c5 0 9 3 9 7a9.77 9.77 0 01-1.562 3.082M15 12a3 3 0 01-3 3m0 0a3 3 0 01-3-3m3 3v.01M3 3l18 18" />
                        </svg>
                      ) : (
                        /* Eye icon */
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {passwordErrors.manualPassword && (
                    <p style={{ color: "#dc2626", fontSize: "12px", marginTop: "4px" }}>
                      {passwordErrors.manualPassword}
                    </p>
                  )}
                </div>

                {/* Confirm Password */}
                <div>
                  <Label text="Confirm Password" required />
                  <div style={{ position: "relative" }}>
                    <input
                      type={showConfirm ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        setPasswordErrors((p) => ({ ...p, confirmPassword: "" }));
                      }}
                      placeholder="Re-enter password"
                      className={inputClass}
                      style={{
                        paddingRight: "40px",
                        ...(passwordErrors.confirmPassword ? { borderColor: "#dc2626" } : {}),
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm((v) => !v)}
                      style={{
                        position: "absolute",
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: "0",
                        color: "#6b7280",
                        display: "flex",
                        alignItems: "center",
                      }}
                      tabIndex={-1}
                      aria-label={showConfirm ? "Hide confirm password" : "Show confirm password"}
                    >
                      {showConfirm ? (
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-5 0-9-4-9-7a9.77 9.77 0 012.168-3.582M6.343 6.343A9.956 9.956 0 0112 5c5 0 9 3 9 7a9.77 9.77 0 01-1.562 3.082M15 12a3 3 0 01-3 3m0 0a3 3 0 01-3-3m3 3v.01M3 3l18 18" />
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {passwordErrors.confirmPassword && (
                    <p style={{ color: "#dc2626", fontSize: "12px", marginTop: "4px" }}>
                      {passwordErrors.confirmPassword}
                    </p>
                  )}
                </div>

              </div>
            )}
          </section>
          {/* ──────────────────────────────────────── */}

          <button
            type="submit"
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 text-white w-full py-3 rounded text-lg"
          >
            {loading ? "Creating..." : "Create Employee"}
          </button>
        </form>
      </div>
    </>
  );
};

export default AddEmployee;
